const MiddlewareDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet Middleware — Global Exception, Secure Headers & Health Pipeline
      </h2>

      <p className="mb-6">
        The <strong>AxisNet.Middleware</strong> package provides foundational
        security, reliability, and diagnostics middleware for every
        AxisNet-based application. These middlewares operate at the ASP.NET Core
        pipeline level, ensuring standardized exception handling, safe HTTP
        responses, and unified health monitoring.
      </p>

      {/* GLOBAL EXCEPTION MODEL */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          GlobalExceptionMessageModel — Unified Error Response Contract
        </h3>

        <p className="mb-4">
          This model defines the standardized error response returned by the
          global exception middleware. It ensures that all unhandled or
          framework-level exceptions produce a consistent JSON structure that
          clients can reliably parse.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Purpose</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Encapsulates HTTP status code of the error.</li>
          <li>Contains a human-readable exception message.</li>
          <li>Includes validation error lists when validation fails.</li>
          <li>
            Ensures all errors follow a unified schema across the entire API.
          </li>
        </ul>

        <p className="mt-2">
          This model is the foundation of AxisNet’s predictable API error
          experience.
        </p>
      </div>

      {/* GLOBAL EXCEPTION MIDDLEWARE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          GlobalExceptionMiddleware — Centralized Error Handling
        </h3>

        <p className="mb-4">
          The <strong>GlobalExceptionMiddleware</strong> intercepts all
          unhandled exceptions occurring anywhere in the ASP.NET Core pipeline.
          It prevents raw stack traces from leaking to clients, formats the
          error properly, and optionally sends a developer notification email.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Key Responsibilities
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Catches every unhandled exception thrown by the application.</li>
          <li>
            Resolves the correct HTTP status code based on exception type.
          </li>
          <li>
            Builds a <strong>GlobalExceptionMessageModel</strong> response.
          </li>
          <li>Formats error data as clean JSON.</li>
          <li>Reads and injects HTML email templates for developer alerts.</li>
          <li>
            Includes contextual metadata such as user ID, timestamp, endpoint,
            and stack trace.
          </li>
          <li>Prevents internal exception details from leaking to clients.</li>
          <li>Automatically logs or reports unexpected errors via email.</li>
        </ul>

        <h4 className="text-lg text-white font-semibold mb-2 mt-4">
          Exception Type Handling
        </h4>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>ValidationException</strong> → 422 Unprocessable Entity
          </li>
          <li>
            <strong>AuthorizationException</strong> → 401 Unauthorized
          </li>
          <li>
            <strong>UnauthorizedAccessException</strong> → 401 Unauthorized
          </li>
          <li>
            <strong>AxisSafeException</strong> → 400 Bad Request
          </li>
          <li>
            <strong>Any other exception</strong> → 500 Internal Server Error
          </li>
        </ul>

        <p className="mt-4">
          This middleware ensures a clean, safe, and informative error pipeline
          across all AxisNet modules.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2 mt-6">
          Required Configuration — Developer Email
        </h4>

        <p className="mb-4">
          The <strong>Developer:Mail</strong> configuration setting is
          <span className="text-emerald-400 font-semibold">mandatory</span> when
          using the AxisNet Global Exception Middleware. This value is used to
          send automated error reports whenever an unexpected or critical
          exception occurs.
        </p>

        <p className="mb-4">
          Without this configuration, the middleware cannot deliver developer
          notifications. Although the API will continue to function, the system
          will lose its ability to automatically inform the developer about
          fatal or production-level errors.
        </p>

        <h5 className="text-md text-white font-semibold mb-2">
          Example (Required in appsettings.json)
        </h5>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`"Developer": {
  "Mail": "youremail@gmail.com"
}`}
        </pre>

        <p className="mt-4">
          When a non-<strong>AxisSafeException</strong> error occurs, the
          middleware:
        </p>

        <ul className="list-disc pl-6 space-y-2 mt-2">
          <li>Loads the HTML-based error report template</li>
          <li>Injects endpoint, stack trace, and user information</li>
          <li>Sends the formatted email to the configured developer address</li>
        </ul>

        <p className="mt-4">
          This guarantees that developers receive critical alerts immediately,
          helping them detect and resolve production issues faster.
        </p>
      </div>

      {/* SECURE HEADER MIDDLEWARE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          SecureHeaderMiddleware — HTTP Response Hardening
        </h3>

        <p className="mb-4">
          The <strong>SecureHeaderMiddleware</strong> injects essential security
          headers into every HTTP response. These headers protect against common
          web vulnerabilities such as clickjacking, MIME sniffing, cross-site
          scripting, and insecure referrer leakage.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">What It Adds</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>X-Frame-Options</strong>: Blocks iframe embedding.
          </li>
          <li>
            <strong>X-Content-Type-Options</strong>: Prevents content type
            spoofing.
          </li>
          <li>
            <strong>Referrer-Policy</strong>: Prevents sensitive referrer
            leakage.
          </li>
          <li>
            <strong>X-XSS-Protection</strong>: Enables browser XSS filter.
          </li>
          <li>
            <strong>Content-Security-Policy</strong>: Restricts allowed content
            sources.
          </li>
        </ul>

        <p className="mt-4">
          This middleware strengthens application security without requiring any
          modification to application logic.
        </p>
      </div>

      {/* AXISNET HEALTH CHECK */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          UseAxisNetHealth — Health Check Endpoints
        </h3>

        <p className="mb-4">
          This extension method configures the application's health check
          endpoints. It exposes both machine-readable and UI-based health
          dashboards enabling developers and monitoring systems to track
          real-time system health.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Endpoints Exposed
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>/health</strong> — JSON-based report detailing service
            dependencies such as SQL Server, Redis, and internal checks.
          </li>
          <li>
            <strong>/health-ui</strong> — Interactive UI dashboard visualizing
            health results.
          </li>
          <li>
            <strong>/health-json</strong> — API endpoint used by the dashboard.
          </li>
        </ul>

        <h4 className="text-lg text-white font-semibold mb-2 mt-4">
          Why This Matters
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Enables real-time monitoring and uptime tracking.</li>
          <li>Quickly identifies issues with external services.</li>
          <li>
            Essential for production deployments requiring DevOps tooling.
          </li>
          <li>Fully integrated with ASP.NET Core HealthChecks ecosystem.</li>
        </ul>
      </div>

      {/* GLOBAL EXCEPTION REGISTRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          UseAxisNetGlobalExceptionMiddleware — Registering the Global Exception
          Pipeline
        </h3>

        <p className="mb-4">
          This extension method activates the GlobalExceptionMiddleware and
          wires it into the ASP.NET Core request pipeline. It also retrieves the
          developer’s email from application configuration so critical errors
          can be reported automatically.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Behavior</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Reads developer contact information from configuration.</li>
          <li>Registers the global exception handler middleware.</li>
          <li>Ensures all downstream exceptions are intercepted.</li>
          <li>Unifies API error formatting.</li>
          <li>Allows automated email notifications for fatal errors.</li>
        </ul>

        <p className="mt-4">
          This middleware is typically placed at the top of the pipeline,
          ensuring complete error interception.
        </p>
      </div>

      {/* SECURE HEADER REGISTRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          UseAxisNetSecureHeaderMiddleware — Enabling HTTP Hardening Layer
        </h3>

        <p className="mb-4">
          This extension method integrates the SecureHeaderMiddleware into the
          pipeline. It ensures every outgoing HTTP response includes strict
          security headers required for modern web applications.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Benefits</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Protects against clickjacking.</li>
          <li>Prevents MIME-type attacks.</li>
          <li>Blocks potential XSS vectors.</li>
          <li>Enforces secure content loading policies.</li>
          <li>Improves application security posture immediately.</li>
        </ul>

        <p className="mt-4">
          This middleware is lightweight and recommended for all production
          deployments.
        </p>
      </div>

      {/* USAGE EXAMPLES */}
      <div className="bg-slate-900/40 border border-slate-700 p-6 rounded-lg mt-16">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Usage Examples — How to Enable AxisNet Middlewares
        </h3>

        <p className="mb-4">
          Below are real-world examples showing how to register the AxisNet
          middlewares inside your <strong>Program.cs</strong> or{" "}
          <strong>Startup.cs</strong>. These examples demonstrate the correct
          ordering and integration strategy.
        </p>

        {/* Example 1 */}
        <h4 className="text-xl text-white font-semibold mb-2">
          1. Registering Global Exception Middleware
        </h4>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`// Program.cs

var builder = WebApplication.CreateBuilder(args);
var app = builder.Build();

// Must be placed at the top of the pipeline
app.UseAxisNetGlobalExceptionMiddleware(builder.Configuration);

app.MapControllers();
app.Run();
`}
        </pre>

        {/* Example 2 */}
        <h4 className="text-xl text-white font-semibold mb-2 mt-6">
          2. Enabling Secure Header Middleware
        </h4>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`// Program.cs

app.UseAxisNetSecureHeaderMiddleware();

// This middleware should appear early in the pipeline
// so headers are applied to all outgoing responses.
`}
        </pre>

        {/* Example 3 */}
        <h4 className="text-xl text-white font-semibold mb-2 mt-6">
          3. Configuring AxisNet Health Check Endpoints
        </h4>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`// Program.cs

app.UseRouting();

// Adds /health, /health-ui and /health-json endpoints
app.UseAxisNetHealth();

app.UseAuthentication();
app.UseAuthorization();

app.MapControllers();
app.Run();
`}
        </pre>

        {/* Example 4 */}
        <h4 className="text-xl text-white font-semibold mb-2 mt-6">
          4. Recommended Final Pipeline Order
        </h4>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`// Program.cs (Recommended Order)

var builder = WebApplication.CreateBuilder(args);
var app = builder.Build();

// 1. Global Exception Handling
app.UseAxisNetGlobalExceptionMiddleware(builder.Configuration);

// 2. Security Hardening
app.UseAxisNetSecureHeaderMiddleware();

// 3. Routing
app.UseRouting();

// 4. Authentication / Authorization
app.UseAuthentication();
app.UseAuthorization();

// 5. Health Check Endpoints
app.UseAxisNetHealth();

// 6. Controllers
app.MapControllers();

app.Run();
`}
        </pre>

        <p className="mt-6">
          These examples ensure a clean, safe, secure, and fully standardized
          middleware pipeline for any AxisNet-based backend. Correct ordering is
          critical for ensuring all features—global exception handling, security
          headers, authentication, and health monitoring—operate reliably.
        </p>
      </div>
    </section>
  );
};

export default MiddlewareDoc;
